import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Phone, ClipboardList, Globe, ChevronDown, Mic, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import RegistrationForm from "@/components/RegistrationForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Agnese avatar
const AGNESE_AVATAR = "https://images.unsplash.com/photo-1610666652096-07e42724bfeb?w=400&q=80";
const AGNESE_AVATAR_LARGE = "https://images.unsplash.com/photo-1610666652096-07e42724bfeb?w=1200&q=90";

const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Country to language mapping
const countryToLanguage = {
  // Italian
  'IT': 'it',
  // English
  'GB': 'en', 'US': 'en', 'AU': 'en', 'CA': 'en', 'NZ': 'en', 'IE': 'en',
  // French
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'MC': 'fr', 'LU': 'fr',
  // German
  'DE': 'de', 'AT': 'de', 'LI': 'de',
  // Spanish
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es', 'PE': 'es', 'VE': 'es'
};

// Detect language from IP geolocation
const detectLanguageFromIP = async () => {
  try {
    // Use free IP geolocation API
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    if (data && data.country_code) {
      const detectedLang = countryToLanguage[data.country_code] || 'en';
      console.log(`🌍 Detected country: ${data.country_code} (${data.country_name}), language: ${detectedLang}`);
      return detectedLang;
    }
  } catch (error) {
    console.log('IP detection failed, using default language');
  }
  return 'it'; // Default to Italian
};

// Language configurations
const languages = {
  it: {
    code: "it",
    name: "Italiano",
    flag: "🇮🇹",
    greeting: "Ciao! 👋 Sono Agnese di Sardinia Kite School! Come posso aiutarti?",
    welcomeBack: "Ciao! 👋 Bentornato! Come posso aiutarti?",
    callToAction: "Vieni a fare kite con noi! 🌊",
    placeholder: "Scrivi un messaggio...",
    whatsapp: "WhatsApp",
    quickReplies: ["Quanto costa?", "Orari lezioni", "Voglio prenotare", "Condizioni meteo", "Dove siete?"],
    formButton: "📋 Compila il Modulo di Iscrizione",
    errorMessage: "Ops! C'è stato un problema 😅 Scrivici su WhatsApp: +39 351 359 0924",
    bookingHint: "📋 Prenota ora",
    wingHint: "🪽 Vuoi provare qualcosa di nuovo, facile e divertente? Prova il Wingsurf/Wingfoil!"
  },
  en: {
    code: "en",
    name: "English",
    flag: "🇬🇧",
    greeting: "Hi! 👋 I'm Agnese from Sardinia Kite School! How can I help you?",
    welcomeBack: "Hi! 👋 Welcome back! How can I help you?",
    callToAction: "Come kite with us! 🌊",
    placeholder: "Write a message...",
    whatsapp: "WhatsApp",
    quickReplies: ["How much?", "Lesson times", "I want to book", "Weather conditions", "Where are you?"],
    formButton: "📋 Fill Registration Form",
    errorMessage: "Oops! Something went wrong 😅 Contact us on WhatsApp: +39 351 359 0924",
    bookingHint: "📋 Book now",
    wingHint: "🪽 Want to try something new, easy and fun? Try Wingsurf/Wingfoil!"
  },
  fr: {
    code: "fr",
    name: "Français",
    flag: "🇫🇷",
    greeting: "Salut! 👋 Je suis Agnese de Sardinia Kite School! Comment puis-je vous aider?",
    welcomeBack: "Salut! 👋 Bon retour! Comment puis-je vous aider?",
    callToAction: "Venez faire du kite avec nous! 🌊",
    placeholder: "Écrivez un message...",
    whatsapp: "WhatsApp",
    quickReplies: ["Combien ça coûte?", "Horaires des cours", "Je veux réserver", "Conditions météo", "Où êtes-vous?"],
    formButton: "📋 Remplir le formulaire d'inscription",
    errorMessage: "Oups! Un problème est survenu 😅 Contactez-nous sur WhatsApp: +39 351 359 0924",
    bookingHint: "📋 Réserver maintenant",
    wingHint: "🪽 Envie d'essayer quelque chose de nouveau, facile et amusant? Essayez le Wingsurf/Wingfoil!"
  },
  de: {
    code: "de",
    name: "Deutsch",
    flag: "🇩🇪",
    greeting: "Hallo! 👋 Ich bin Agnese von Sardinia Kite School! Wie kann ich Ihnen helfen?",
    welcomeBack: "Hallo! 👋 Willkommen zurück! Wie kann ich Ihnen helfen?",
    callToAction: "Komm kiten mit uns! 🌊",
    placeholder: "Nachricht schreiben...",
    whatsapp: "WhatsApp",
    quickReplies: ["Wie viel kostet es?", "Unterrichtszeiten", "Ich möchte buchen", "Wetterbedingungen", "Wo sind Sie?"],
    formButton: "📋 Anmeldeformular ausfüllen",
    errorMessage: "Ups! Etwas ist schief gelaufen 😅 Kontaktieren Sie uns auf WhatsApp: +39 351 359 0924",
    bookingHint: "📋 Jetzt buchen",
    wingHint: "🪽 Lust auf etwas Neues, Einfaches und Lustiges? Probieren Sie Wingsurf/Wingfoil!"
  },
  es: {
    code: "es",
    name: "Español",
    flag: "🇪🇸",
    greeting: "¡Hola! 👋 Soy Agnese de Sardinia Kite School! ¿Cómo puedo ayudarte?",
    welcomeBack: "¡Hola! 👋 ¡Bienvenido de nuevo! ¿Cómo puedo ayudarte?",
    callToAction: "¡Ven a hacer kite con nosotros! 🌊",
    placeholder: "Escribe un mensaje...",
    whatsapp: "WhatsApp",
    quickReplies: ["¿Cuánto cuesta?", "Horarios de clases", "Quiero reservar", "Condiciones del tiempo", "¿Dónde están?"],
    formButton: "📋 Completar formulario de inscripción",
    errorMessage: "¡Ups! Algo salió mal 😅 Contáctanos en WhatsApp: +39 351 359 0924",
    bookingHint: "📋 Reservar ahora",
    wingHint: "🪽 ¿Quieres probar algo nuevo, fácil y divertente? ¡Prueba el Wingsurf/Wingfoil!"
  }
};

export default function AgneseChatPage() {
  const [language, setLanguage] = useState("it");
  const [languageDetected, setLanguageDetected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [suggestedActivity, setSuggestedActivity] = useState("");
  const [agneseState, setAgneseState] = useState("waving");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem("agnese_session_id");
    if (stored) return stored;
    const newId = generateId();
    localStorage.setItem("agnese_session_id", newId);
    return newId;
  });
  
  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [showBookingHint, setShowBookingHint] = useState(true);
  const [showWingHint, setShowWingHint] = useState(false);
  const [showWhatsappBottom, setShowWhatsappBottom] = useState(false);
  const lang = languages[language];

  // Timed hints - booking hint appears after 10s for 10s, then wing hint for 10s, WhatsApp after 30s
  useEffect(() => {
    // Hide booking hint after 30 seconds, show wing hint
    const hideBookingTimer = setTimeout(() => {
      setShowBookingHint(false);
      setShowWingHint(true);
    }, 30000);
    
    // Hide wing hint after 40 seconds
    const hideWingTimer = setTimeout(() => {
      setShowWingHint(false);
    }, 40000);

    // Show WhatsApp after 30 seconds
    const whatsappTimer = setTimeout(() => {
      setShowWhatsappBottom(true);
    }, 30000);
    
    return () => {
      clearTimeout(hideBookingTimer);
      clearTimeout(hideWingTimer);
      clearTimeout(whatsappTimer);
    };
  }, []);

  // Auto-detect language from IP on first load
  useEffect(() => {
    const initLanguage = async () => {
      // Check if user MANUALLY set a language (not auto-detected)
      const manualLang = localStorage.getItem("agnese_language_manual");
      if (manualLang) {
        setLanguage(manualLang);
        setLanguageDetected(true);
        return;
      }
      
      // Auto-detect from IP every time
      const detectedLang = await detectLanguageFromIP();
      setLanguage(detectedLang);
      setLanguageDetected(true);
    };
    initLanguage();
  }, []);

  // Initialize greeting message
  useEffect(() => {
    if (languageDetected) {
      setMessages([{ role: "assistant", content: lang.greeting }]);
    }
  }, [language, languageDetected, lang.greeting]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasInteracted) {
        setAgneseState("normal");
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasInteracted]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/history/${sessionId}`);
        if (response.data && response.data.length > 0) {
          setMessages([
            { role: "assistant", content: lang.welcomeBack },
            ...response.data.map(msg => ({ role: msg.role, content: msg.content }))
          ]);
          setHasInteracted(true);
          setAgneseState("smiling");
        }
      } catch (error) {
        console.log("No previous chat history");
      }
    };
    loadHistory();
  }, [sessionId, lang.welcomeBack]);

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    // Save as MANUAL preference (overrides auto-detection)
    localStorage.setItem("agnese_language_manual", newLang);
    triggerSmile();
    
    // Show warning for French and German about instructor availability
    if (newLang === "fr" || newLang === "de") {
      const warningMsg = newLang === "fr" 
        ? "⚠️ Note: Pour les cours en français, la disponibilité d'un instructeur francophone n'est pas garantie. Veuillez confirmer via WhatsApp: +39 351 359 0924"
        : "⚠️ Hinweis: Für Unterricht auf Deutsch ist die Verfügbarkeit eines deutschsprachigen Lehrers nicht garantiert. Bitte bestätigen Sie per WhatsApp: +39 351 359 0924";
      
      setTimeout(() => {
        setMessages(prev => [...prev, { role: "assistant", content: warningMsg }]);
      }, 500);
    }
  };

  const triggerSmile = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    setAgneseState("smiling");
    setTimeout(() => setAgneseState("normal"), 2000);
  };

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    triggerSmile();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: textToSend }]);
    setIsLoading(true);

    // Add language instruction to the message
    const messageWithLang = `[Rispondi in ${lang.name}] ${textToSend}`;

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: messageWithLang
      });

      const aiResponse = response.data.response;
      
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: aiResponse }
      ]);

      // Auto-play TTS if enabled
      if (ttsEnabled && aiResponse) {
        playTTS(aiResponse);
      }

      if (response.data.show_form) {
        if (response.data.suggested_activity) {
          setSuggestedActivity(response.data.suggested_activity);
        }
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { role: "system", content: "form_button" }
          ]);
        }, 500);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: lang.errorMessage }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Text-to-Speech function
  const playTTS = async (text) => {
    try {
      // Stop any current audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      
      setIsSpeaking(true);
      setAgneseState("speaking");
      
      const response = await axios.post(`${API}/tts`, {
        text: text,
        voice: "nova" // Friendly female voice for Agnese
      });
      
      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(response.data.audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      
      audio.onended = () => {
        setIsSpeaking(false);
        setAgneseState("smiling");
        setTimeout(() => setAgneseState("normal"), 1000);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        setAgneseState("normal");
      };
      
      await audio.play();
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
      setAgneseState("normal");
    }
  };
  
  // Stop TTS
  const stopTTS = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setAgneseState("normal");
  };

  // Speech-to-Text functions - Hold to talk like WhatsApp
  const recordingRef = useRef(false);
  
  const startRecording = async (e) => {
    if (e) e.preventDefault();
    if (recordingRef.current || isLoading) return;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size < 1000) {
          console.log("Recording too short, ignoring");
          return;
        }
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          
          const response = await axios.post(`${API}/stt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data.text && response.data.text.trim()) {
            handleSend(response.data.text.trim());
          }
        } catch (error) {
          console.error("STT error:", error);
        }
      };
      
      mediaRecorder.start();
      recordingRef.current = true;
      setIsRecording(true);
      triggerSmile();
    } catch (error) {
      console.error("Microphone access error:", error);
    }
  };
  
  const stopRecording = (e) => {
    if (e) e.preventDefault();
    if (mediaRecorderRef.current && recordingRef.current) {
      mediaRecorderRef.current.stop();
      recordingRef.current = false;
      setIsRecording(false);
    }
  };

  const toggleRecording = (e) => {
    if (e) e.preventDefault();
    if (recordingRef.current) {
      stopRecording(e);
    } else {
      startRecording(e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReplyClick = (text) => {
    triggerSmile();
    handleSend(text);
  };

  const avatarVariants = {
    waving: {
      rotate: [0, -10, 10, -10, 10, 0],
      scale: [1, 1.05, 1.05, 1.05, 1.05, 1],
      transition: { duration: 1.5, repeat: Infinity, repeatDelay: 1 }
    },
    speaking: {
      scale: [1, 1.05, 1, 1.05, 1],
      transition: { duration: 0.8, repeat: Infinity }
    },
    smiling: {
      scale: [1, 1.1, 1.05],
      transition: { duration: 0.5 }
    },
    normal: {
      scale: 1,
      rotate: 0
    }
  };

  return (
    <div 
      className="h-screen w-full flex flex-col overflow-hidden bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500" 
      data-testid="agnese-chat"
      onClick={() => !hasInteracted && triggerSmile()}
    >
      
      {/* Language Selector - Top Left */}
      <div className="absolute top-4 left-4 z-50">
        <div className="flex flex-col items-start gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full px-3 py-2 h-auto"
                data-testid="language-selector"
              >
                <Globe className="w-4 h-4 mr-2" />
                <span className="text-lg mr-1">{lang.flag}</span>
                <span className="text-sm font-medium">{lang.name}</span>
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {Object.values(languages).map((l) => (
                <DropdownMenuItem 
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={`cursor-pointer ${language === l.code ? 'bg-teal-50' : ''}`}
                  data-testid={`lang-${l.code}`}
                >
                  <span className="text-lg mr-2">{l.flag}</span>
                  <span>{l.name}</span>
                  {language === l.code && <span className="ml-auto text-teal-600">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Arrow hint for language change - below the button */}
          <motion.div 
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="flex items-center text-white/80 text-xs ml-2"
          >
            <span className="mr-1">↑</span>
            <span>change language</span>
          </motion.div>
        </div>
      </div>

      {/* Header con foto grande di Agnese */}
      <motion.div 
        className="relative flex-shrink-0"
        animate={{ 
          height: inputFocused ? "auto" : "auto",
          paddingTop: inputFocused ? "1rem" : "2rem",
          paddingBottom: inputFocused ? "0.5rem" : "1.5rem"
        }}
        transition={{ duration: 0.3 }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center blur-2xl opacity-40"
          style={{ backgroundImage: `url(${AGNESE_AVATAR_LARGE})` }}
        />
        
        <div className="relative px-4 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: inputFocused ? 0.6 : 1, 
              opacity: 1 
            }}
            transition={{ duration: 0.3 }}
            className="relative inline-block"
          >
            <motion.div 
              className="relative mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl"
              style={{ 
                width: inputFocused ? "4rem" : "9rem",
                height: inputFocused ? "4rem" : "9rem"
              }}
              variants={avatarVariants}
              animate={agneseState}
            >
              <img src={AGNESE_AVATAR_LARGE} alt="Agnese" className="w-full h-full object-cover object-top" />
            </motion.div>
            
            {!inputFocused && (
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute top-2 right-2 w-6 h-6 bg-green-400 rounded-full border-3 border-white shadow-lg"
              />
            )}
            
            <AnimatePresence>
              {agneseState === "waving" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0, x: 20, y: -20 }}
                  animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-3xl"
                >
                  <motion.span
                    animate={{ rotate: [0, 20, -20, 20, -20, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    👋
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {agneseState === "smiling" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-2xl"
                >
                  😊
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {agneseState === "speaking" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-2xl"
                >
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    🗣️
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: inputFocused ? 0 : 1, height: inputFocused ? 0 : "auto" }}
            transition={{ delay: 0.3 }}
            className={inputFocused ? "overflow-hidden" : ""}
          >
            <h1 className={`mt-2 font-bold text-white drop-shadow-lg ${inputFocused ? "text-lg" : "text-3xl md:text-4xl"}`}>Agnese</h1>
            <p className="text-white/90 text-sm md:text-base mt-1">Sardinia Wing Kite School</p>
            
            {/* "Prenota ora" - sempre visibile, cliccabile per aprire il modulo */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setShowForm(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-3 px-4 py-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full text-sm font-semibold border border-white/30 transition-all"
              data-testid="quick-booking-btn"
            >
              {lang.bookingHint}
            </motion.button>
            
            {/* Pulsante Wing promo - visible after 5s for 10s */}
            <AnimatePresence>
              {showWingHint && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  onClick={() => {
                    setShowWingHint(false);
                    // Send message to get wing info
                    const wingMessage = language === 'it' 
                      ? "Dimmi di più sul wingsurf e wingfoil!" 
                      : language === 'en' ? "Tell me more about wingsurf and wingfoil!"
                      : language === 'fr' ? "Parlez-moi du wingsurf et wingfoil!"
                      : language === 'de' ? "Erzählen Sie mir mehr über Wingsurf und Wingfoil!"
                      : "¡Cuéntame más sobre wingsurf y wingfoil!";
                    setInput(wingMessage);
                    setTimeout(() => handleSend(), 100);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-3 px-4 py-2 bg-gradient-to-r from-purple-500/80 to-pink-500/80 backdrop-blur-sm hover:from-purple-600/80 hover:to-pink-600/80 text-white rounded-full text-sm font-medium border border-white/30 transition-all"
                  data-testid="wing-promo-btn"
                >
                  {lang.wingHint}
                </motion.button>
              )}
            </AnimatePresence>
            
            <AnimatePresence>
              {agneseState === "waving" && !hasInteracted && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 text-white/80 text-sm"
                >
                  {lang.callToAction}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>

      {/* Chat Container */}
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", damping: 20 }}
        className="flex-1 bg-white rounded-t-[2.5rem] shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="chat-messages">
          <AnimatePresence>
            {messages.map((msg, i) => (
              <motion.div
                key={`${msg.role}-${i}`}
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "system" && msg.content === "form_button" ? (
                  <motion.button
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { triggerSmile(); setShowForm(true); }}
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="open-form-btn"
                  >
                    <ClipboardList className="w-5 h-5" />
                    {lang.formButton}
                  </motion.button>
                ) : msg.role !== "system" && (
                  <>
                    {msg.role === "assistant" && (
                      <div className="w-9 h-9 rounded-full overflow-hidden mr-2 flex-shrink-0 border-2 border-teal-200 shadow-md">
                        <img src={AGNESE_AVATAR} alt="Agnese" className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] px-4 py-3 rounded-2xl shadow-sm ${
                        msg.role === "user"
                          ? "bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-br-md"
                          : "bg-gray-100 text-gray-800 rounded-bl-md"
                      }`}
                    >
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          
          {isLoading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-teal-200 shadow-md">
                <img src={AGNESE_AVATAR} alt="Agnese" className="w-full h-full object-cover object-top" />
              </div>
              <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        {messages.length <= 2 && !hasInteracted && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="px-4 pb-2"
          >
            <div className="flex flex-wrap gap-2 justify-center">
              {lang.quickReplies.map((text, i) => (
                <button
                  key={`reply-${text.slice(0, 10)}`}
                  onClick={() => handleQuickReplyClick(text)}
                  className="bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 text-teal-700 text-sm px-4 py-2 rounded-full transition-all border border-teal-200 shadow-sm hover:shadow-md active:scale-95"
                  data-testid={`quick-reply-${i}`}
                >
                  {text}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Input Area */}
        <div className="p-3 pb-4 border-t border-gray-100 bg-white/95 backdrop-blur-sm safe-area-bottom">
          <div className="max-w-lg mx-auto">
            {/* Input row - full width */}
            <div className="flex gap-2 items-center">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => { triggerSmile(); setInputFocused(true); }}
                onBlur={() => setInputFocused(false)}
                placeholder={lang.placeholder}
                className="flex-1 rounded-full border-gray-200 bg-gray-50 focus:bg-white focus:border-teal-400 focus:ring-teal-400 h-12 px-5 text-base"
                disabled={isLoading || isRecording}
                data-testid="chat-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="rounded-full w-12 h-12 p-0 shrink-0 bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="send-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            {/* Icons row - below input */}
            <div className="flex items-center justify-center gap-3 mt-2">
              <Button
                onClick={() => setShowForm(true)}
                variant="ghost"
                className="rounded-full w-10 h-10 p-0 text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                data-testid="attach-btn"
              >
                <ClipboardList className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => {
                  if (isSpeaking) {
                    stopTTS();
                  } else {
                    setTtsEnabled(!ttsEnabled);
                  }
                }}
                variant="ghost"
                className={`rounded-full w-10 h-10 p-0 transition-all ${
                  isSpeaking 
                    ? "text-orange-500 animate-pulse" 
                    : ttsEnabled 
                      ? "text-teal-500 hover:text-teal-600 hover:bg-teal-50" 
                      : "text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                }`}
                data-testid="tts-toggle"
              >
                {isSpeaking || ttsEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>
              <Button
                onClick={toggleRecording}
                variant="ghost"
                className={`rounded-full w-10 h-10 p-0 select-none transition-all ${
                  isRecording 
                    ? "text-red-500 animate-pulse scale-110 bg-red-50" 
                    : "text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                }`}
                disabled={isLoading}
                data-testid="mic-btn"
              >
                <Mic className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* WhatsApp link - appears after 30 seconds */}
          <AnimatePresence>
            {showWhatsappBottom && (
              <motion.a
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                href="https://wa.me/393513590924"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 mt-2 py-1 text-sm text-gray-400 hover:text-green-600 transition-colors"
                data-testid="whatsapp-bottom-link"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>WhatsApp: +39 351 359 0924</span>
              </motion.a>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Registration Form Modal */}
      <RegistrationForm 
        isOpen={showForm} 
        onClose={() => setShowForm(false)} 
        sessionId={sessionId}
        language={language}
        suggestedActivity={suggestedActivity}
      />
    </div>
  );
}
